import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, FormControl, FormLabel, Grid, MenuItem, OutlinedInput, SelectChangeEvent, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { useGetProjectQuery, useUpdateProjectMutation } from "fond/api";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { getCurrentProject } from "fond/project";
import { Store, SystemsOfMeasurement } from "fond/types";
import { Actions } from "fond/utils/permissions";
import { isCollaborationProject } from "fond/utils/project";
import { Modal } from "fond/widgets";

import { SelectHelperText, ShortSelect } from "./projectSettings.styles";

interface ProjectSettingsModalProps {
  onClose: () => void;
}

const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({ onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const projectId = useSelector((state: Store) => state.project.projectId);
  const { data: project } = useGetProjectQuery(projectId);
  const [systemOfMeasurement, setSystemOfMeasurement] = useState(project?.SystemOfMeasurement);
  const [updateProject] = useUpdateProjectMutation();
  const currentProject = useSelector((state: Store) => getCurrentProject(state.project));

  const canEditSettings = usePermissionCheck(
    isCollaborationProject(currentProject) ? Actions.PROJECT_EDIT : Actions.PLANNER_PROJECT_EDIT,
    currentProject?.Permission.Level
  );

  const handleOnSave = async () => {
    if (project && systemOfMeasurement) {
      analytics.track("Update system of measurement", { projectId, systemOfMeasurement, source: "project-settings" });
      try {
        await updateProject({ ID: project.ID, SystemOfMeasurement: systemOfMeasurement }).unwrap();

        enqueueSnackbar("System of measurement updated.");
        onClose();
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const handleChange = (e: SelectChangeEvent<`${SystemsOfMeasurement}`>) => {
    setSystemOfMeasurement(e.target.value as `${SystemsOfMeasurement}`); // Mui select value does not get type narrowed with generics
  };

  const handleOnClose = () => {
    analytics.track("Closed project setting modal");
    onClose();
  };

  return (
    <Modal
      open
      header="Project settings"
      onClose={handleOnClose}
      data-testid="project-settings"
      content={
        <Grid container direction="column">
          <Typography>General options for the current project.</Typography>
          <Box mt={2}>
            <FormControl fullWidth>
              <FormLabel>Unit type</FormLabel>
              <ShortSelect
                value={systemOfMeasurement}
                data-testid="measurement-input"
                onChange={handleChange}
                input={<OutlinedInput size="small" />}
                margin="dense"
                disabled={!canEditSettings}
              >
                <MenuItem value="imperial">Imperial</MenuItem>
                <MenuItem value="metric">Metric</MenuItem>
              </ShortSelect>
              <SelectHelperText>Determines the unit of measurements the project will use.</SelectHelperText>
            </FormControl>
          </Box>
        </Grid>
      }
      actions={
        <>
          <Button data-testid="close-button" onClick={handleOnClose}>
            Close
          </Button>
          <Button data-testid="save-button" variant="contained" disabled={!canEditSettings} color="primary" onClick={handleOnSave}>
            Update Settings
          </Button>
        </>
      }
    />
  );
};

interface ProjectSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({ isOpen, onClose }) => {
  // We unmount the modal when the settings are closed to reset internal state - this prevents stale data
  // from being shown if the modal is reopened after an external change is made.
  if (!isOpen) {
    return null;
  }

  return <ProjectSettingsModal onClose={onClose} />;
};

export default ProjectSettings;
